import { forwardRef, useState } from 'react'
import { Eye, EyeOff, Search as SearchIcon, X } from 'lucide-react'
import { useControllableValue } from '@/ui/kit'
import { cn } from './internal/utils'
import {
  CONTROL_SIZES,
  FIELD_SURFACE,
  FOCUS_RING,
  RADIUS_CONTROL,
  STATUS_FIELD,
} from './internal/presets'

const SIZE_CLASSES = CONTROL_SIZES

const fieldClasses = (size, status, disabled, extra) =>
  cn(
    FIELD_SURFACE,
    'w-full text-[var(--text-dark)] placeholder:text-[var(--text-muted)] transition-colors',
    RADIUS_CONTROL,
    SIZE_CLASSES[size] || SIZE_CLASSES.middle,
    STATUS_FIELD[status],
    disabled && 'ui-input-disabled',
    extra
  )

/** Wraps the control when antd's prefix/suffix/addon slots are used. */
const AffixWrapper = ({ size, status, disabled, prefix, suffix, className, children }) => (
  <span
    className={cn(
      'ui-input-affix-wrapper flex items-center gap-2',
      FIELD_SURFACE,
      RADIUS_CONTROL,
      SIZE_CLASSES[size] || SIZE_CLASSES.middle,
      STATUS_FIELD[status],
      disabled && 'ui-input-disabled',
      className
    )}
  >
    {prefix ? <span className="ui-input-prefix shrink-0 text-[var(--gray-600)]">{prefix}</span> : null}
    {children}
    {suffix ? <span className="ui-input-suffix shrink-0 text-[var(--gray-600)]">{suffix}</span> : null}
  </span>
)

const withAddons = (node, addonBefore, addonAfter) => {
  if (!addonBefore && !addonAfter) return node
  return (
    <span className="ui-input-group-wrapper flex w-full items-stretch">
      {addonBefore ? (
        <span
          className={cn(
            'ui-input-group-addon flex items-center border border-r-0 bg-[var(--gray-100)] px-3 text-sm',
            BORDER,
            'rounded-l-[var(--radius-sm)]'
          )}
        >
          {addonBefore}
        </span>
      ) : null}
      <span className="min-w-0 flex-1 [&>*]:rounded-none">{node}</span>
      {addonAfter ? (
        <span
          className={cn(
            'ui-input-group-addon flex items-center border border-l-0 bg-[var(--gray-100)] px-3 text-sm',
            BORDER,
            'rounded-r-[var(--radius-sm)]'
          )}
        >
          {addonAfter}
        </span>
      ) : null}
    </span>
  )
}

const Input = forwardRef(
  (
    {
      size = 'middle',
      status,
      prefix,
      suffix,
      addonBefore,
      addonAfter,
      allowClear = false,
      maxLength,
      showCount = false,
      disabled = false,
      value,
      defaultValue,
      onChange,
      className,
      style,
      variant,
      ...rest
    },
    ref
  ) => {
    const [innerValue, setInnerValue] = useControllableValue(value, defaultValue ?? '')

    const handleChange = (event) => {
      setInnerValue(event.target.value)
      onChange?.(event)
    }

    const clear = () => {
      setInnerValue('')
      // antd hands onChange a synthetic-looking event when clearing.
      onChange?.({ target: { value: '' }, currentTarget: { value: '' } })
    }

    const clearButton =
      allowClear && String(innerValue ?? '').length > 0 ? (
        <button
          type="button"
          aria-label="Effacer"
          onClick={clear}
          className="ui-input-clear-icon inline-flex h-4 w-4 items-center justify-center rounded-full text-[var(--gray-600)] hover:text-[var(--text-body)]"
        >
          <X size={12} aria-hidden="true" />
        </button>
      ) : null

    const counter =
      showCount && maxLength ? (
        <span className="ui-input-show-count-suffix text-xs text-[var(--gray-600)]">
          {String(innerValue ?? '').length}/{maxLength}
        </span>
      ) : null

    const needsWrapper = Boolean(prefix || suffix || clearButton || counter)

    const control = (
      <input
        ref={ref}
        value={innerValue ?? ''}
        onChange={handleChange}
        disabled={disabled}
        maxLength={maxLength}
        aria-invalid={status === 'error' || undefined}
        className={
          needsWrapper
            ? cn('ui-input min-w-0 flex-1 border-0 bg-transparent p-0 outline-none', className)
            : fieldClasses(size, status, disabled, cn('ui-input', className))
        }
        style={needsWrapper ? undefined : style}
        {...rest}
      />
    )

    const wrapped = needsWrapper ? (
      <AffixWrapper
        size={size}
        status={status}
        disabled={disabled}
        prefix={prefix}
        suffix={
          <>
            {counter}
            {clearButton}
            {suffix}
          </>
        }
        className={cn(className, style && '')}
      >
        {control}
      </AffixWrapper>
    ) : (
      control
    )

    return withAddons(wrapped, addonBefore, addonAfter)
  }
)
Input.displayName = 'Input'

const TextArea = forwardRef(
  (
    {
      rows = 3,
      autoSize,
      size = 'middle',
      status,
      disabled = false,
      maxLength,
      showCount = false,
      value,
      defaultValue,
      onChange,
      className,
      style,
      ...rest
    },
    ref
  ) => {
    const [innerValue, setInnerValue] = useControllableValue(value, defaultValue ?? '')
    const autoSizeConfig = typeof autoSize === 'object' && autoSize !== null ? autoSize : {}

    const handleChange = (event) => {
      setInnerValue(event.target.value)
      onChange?.(event)
    }

    return (
      <span className="ui-input-textarea relative block w-full">
        <textarea
          ref={ref}
          rows={autoSize ? (autoSizeConfig.minRows ?? rows) : rows}
          value={innerValue ?? ''}
          onChange={handleChange}
          disabled={disabled}
          maxLength={maxLength}
          aria-invalid={status === 'error' || undefined}
          className={fieldClasses(size, status, disabled, cn('ui-input resize-y', className))}
          style={{
            minHeight: autoSizeConfig.minRows ? autoSizeConfig.minRows * 24 + 20 : undefined,
            maxHeight: autoSizeConfig.maxRows ? autoSizeConfig.maxRows * 24 + 20 : undefined,
            ...style,
          }}
          {...rest}
        />
        {showCount && maxLength ? (
          <span className="ui-input-data-count absolute -bottom-5 right-0 text-xs text-[var(--gray-600)]">
            {String(innerValue ?? '').length}/{maxLength}
          </span>
        ) : null}
      </span>
    )
  }
)
TextArea.displayName = 'Input.TextArea'

const Password = forwardRef(({ visibilityToggle = true, iconRender, ...rest }, ref) => {
  const [visible, setVisible] = useState(false)

  return (
    <Input
      ref={ref}
      type={visible ? 'text' : 'password'}
      suffix={
        visibilityToggle ? (
          <button
            type="button"
            aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            onClick={() => setVisible((previous) => !previous)}
            className="inline-flex h-5 w-5 items-center justify-center text-[var(--gray-600)] hover:text-[var(--text-body)]"
          >
            {iconRender
              ? iconRender(visible)
              : visible
                ? <EyeOff size={14} aria-hidden="true" />
                : <Eye size={14} aria-hidden="true" />}
          </button>
        ) : null
      }
      {...rest}
    />
  )
})
Password.displayName = 'Input.Password'

const Search = forwardRef(
  ({ onSearch, enterButton = false, loading = false, value, defaultValue, onChange, ...rest }, ref) => {
    const [innerValue, setInnerValue] = useControllableValue(value, defaultValue ?? '')

    const handleChange = (event) => {
      setInnerValue(event.target.value)
      onChange?.(event)
    }

    const submit = () => onSearch?.(innerValue)

    return (
      <span className="ui-input-search flex w-full items-stretch gap-2">
        <Input
          ref={ref}
          value={innerValue}
          onChange={handleChange}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit()
          }}
          suffix={
            enterButton ? null : (
              <button
                type="button"
                aria-label="Rechercher"
                onClick={submit}
                className="inline-flex h-5 w-5 items-center justify-center text-[var(--gray-600)] hover:text-[var(--primary-color)]"
              >
                <SearchIcon size={14} aria-hidden="true" />
              </button>
            )
          }
          {...rest}
        />
        {enterButton ? (
          <button
            type="button"
            onClick={submit}
            disabled={loading}
            className={cn(
              'ui-btn ui-btn-primary inline-flex min-h-[var(--control-height)] shrink-0 items-center gap-2 bg-[var(--primary-color)] px-4 text-sm font-medium text-[var(--on-primary)]',
              RADIUS_CONTROL,
              FOCUS_RING
            )}
          >
            {enterButton === true ? <SearchIcon size={16} aria-hidden="true" /> : enterButton}
          </button>
        ) : null}
      </span>
    )
  }
)
Search.displayName = 'Input.Search'

const Group = ({ compact, className, children, ...rest }) => (
  <span
    className={cn('ui-input-group flex items-stretch', compact && 'ui-input-group-compact', className)}
    {...rest}
  >
    {children}
  </span>
)

const OTP = forwardRef(({ length = 6, onChange, ...rest }, ref) => (
  <Input ref={ref} maxLength={length} onChange={onChange} inputMode="numeric" {...rest} />
))
OTP.displayName = 'Input.OTP'

Input.TextArea = TextArea
Input.Password = Password
Input.Search = Search
Input.Group = Group
Input.OTP = OTP

export { Password, Search, TextArea, fieldClasses }
export default Input
